import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Button, MessagePlugin } from 'tdesign-react';
import { isEmpty, isNil } from 'lodash';
import { useNavigate } from 'react-router-dom';
import ListPanel from '@/components/ListPanel';
import { isVoid } from '@/utils/common';
import {
  fetchScriptInitVariables,
  resetScript,
  setCmpVariables,
  setIpVariables,
} from '@/slices/execScriptSlice';
import { submitScript } from '@/services/execScriptService';
import Variables, { validateAll } from '../components/Variables';
import SelectHost from './SelectHost';
import SelectScript from './SelectScript';
import SelectCluster from './SelectCluster';

function ExecutiveScript({
  codeContent,
  hosts,
  resetScript,
  fetchScriptInitVariables,
  template,
  ipVariables,
  cmpVariables,
  setCmpVariables,
  setIpVariables,
  cluster,
  k8s,
  isTemp,
}) {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  // 已选择集群
  const clusterSelected = !isNil(cluster);
  // k8s容器 clusterTypeOptions 1
  const isK8s = cluster?.type === 1;

  // 提交参数参数
  const getParams = () => {
    const params = {
      clusterId: cluster.id,
      scriptContend: codeContent,
      variableList: [],
      ipValuevariableList: [],
    };
    // 来自k8s
    if (isK8s) {
      Object.assign(params, { ipList: k8s.id });

      // 如果是模板脚本，额外带参数
      if (!isTemp) {
        Object.assign(params, {
          scriptId: template.id,
          variableList: cmpVariables.map((item) => ({
            currenValue: item.defaultValue,
            key: item.key,
            scriptStencilVariableId: item.originId,
          })),
        });
      }
    } else {
      // 主机提交
      Object.assign(params, { ipList: hosts.map((item) => item.id).join(',') });

      // 如果是模板脚本，额外带参数
      if (!isTemp) {
        Object.assign(params, {
          scriptId: template.id,
          variableList: cmpVariables.map((item) => ({
            currenValue: item.defaultValue,
            key: item.key,
            scriptStencilVariableId: item.originId,
          })),
          ipValuevariableList: ipVariables.map((item) => ({
            currenValue: item.defaultValue,
            key: item.key,
            ip: item.host?.id,
            scriptStencilVariableId: item.originId,
          })),
        });
      }
    }
    return params;
  };

  const doSubmit = async () => {
    if (!isK8s && isEmpty(hosts)) {
      MessagePlugin.warning('目标机器不可为空！');
      return;
    }
    if (isVoid(codeContent.trim())) {
      MessagePlugin.warning('脚本不可为空！');
      return;
    }
    // 校验填写
    if (!validateAll(cmpVariables, ipVariables)) {
      return;
    }

    try {
      setSubmitting(true);
      const {
        data: { id, clusterId },
      } = await submitScript(getParams());
      MessagePlugin.success('操作成功');
      navigate(`/maintenance/executive/record/detail?id=${id}&clusterId=${clusterId}`);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // 没选集群或者模版
    if (!cluster || !template) {
      return;
    }
    const ipList = hosts.map((item) => item.id).join(',');
    // 主机有IPList 或者 k8s 情况下可以请求
    const canFetch = (cluster.type === 0 && ipList) || cluster.type === 1;
    if (canFetch) {
      fetchScriptInitVariables({
        scriptId: template.id,
        clusterId: cluster.id,
        ipList,
      });
    }
  }, [hosts, fetchScriptInitVariables, template, cluster]);

  // 释放时初始化
  useEffect(() => {
    return () => {
      resetScript();
    };
  }, [resetScript]);

  return (
    <ListPanel
      header='执行脚本'
      tip='可以执行临时脚本及脚本模板中的脚本。先选择执行的集群、机器，再寻找要执行的脚本'
      noPadding
    >
      <Root>
        <Container>
          <Left>
            <SelectCluster />
            {/* K8s无需选主机 */}
            {clusterSelected && !isK8s && <SelectHost />}
            {clusterSelected && <SelectScript />}
          </Left>
          {template && (
            <Variables
              style={{ flex: 1 }}
              cmpVariables={cmpVariables}
              ipVariables={ipVariables}
              setCmpVariables={setCmpVariables}
              setIpVariables={setIpVariables}
              hosts={hosts}
              scopeType={cluster.type}
            />
          )}
        </Container>
        {clusterSelected && (
          <Footer>
            <Button theme='primary' onClick={doSubmit} loading={submitting}>
              提交
            </Button>
          </Footer>
        )}
      </Root>
    </ListPanel>
  );
}

export default connect(
  (state) => ({
    hosts: state.execScript.hosts || [],
    codeContent: state.execScript.codeContent,
    template: state.execScript.template,
    cmpVariables: state.execScript.cmpVariables,
    ipVariables: state.execScript.ipVariables,
    cluster: state.execScript.cluster,
    k8s: state.execScript.k8s,
    isTemp: state.execScript.isTemp,
  }),
  {
    resetScript,
    setIpVariables,
    setCmpVariables,
    fetchScriptInitVariables,
  },
)(ExecutiveScript);

const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 150px);
`;

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 20px;
  width: 0;
`;

const Footer = styled.div`
  border-top: 1px solid #e7e7e7;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 24px;
`;
